import { memo } from "react";
import { makeStyles } from "tss-react/mui";
import { MiIcon, MiIconButton } from "@miplatform/components";
import { APP_BAR_HEIGHT, PERMISSIONS } from "@miplatform/constants";
import { useRouter } from "@miplatform/hooks";
import { mdiCog } from "@mdi/js";
import { usePermissions } from "@mibuildweb/hooks";
import {
  AppBar,
  Box,
  Grid,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { HeaderLogo } from "./HeaderLogo/HeaderLogo";
import { SearchBar } from "./SearchBar/SearchBar";
import { UserMenu } from "./UserMenu/UserMenu";

interface MiAppBarProps {
  tenantName?: string;
  appBarColor?: string;
  appLogo?: React.ReactNode;
  hideSearchBar?: boolean;
}

export const MiAppBar = ({
  tenantName = "",
  appBarColor,
  appLogo,
  hideSearchBar,
}: MiAppBarProps) => {
  const { classes } = useStyles({
    appBarColor,
  });
  const isTablet = useMediaQuery((theme) => theme.breakpoints.down("md"));

  return (
    <AppBar
      position="relative"
      elevation={0}
      data-testid="mi-appbar"
      sx={{ minHeight: APP_BAR_HEIGHT }}
    >
      <Toolbar className={classes.toolBarContainer}>
        <Grid
          container
          size={12}
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Grid size={{ xs: isTablet ? 8 : 2 }}>
            <Box className={classes.leftContainer}>
              {appLogo ?? <HeaderLogo />}
              <Box className={classes.divider} />
              <Typography className={classes.tenant}>{tenantName}</Typography>
            </Box>
          </Grid>

          {!hideSearchBar && !isTablet && (
            <Grid size={{ xs: 4 }} className={classes.searchBar}>
              <SearchBar />
            </Grid>
          )}

          <Grid
            size={{ xs: 1 }}
            sx={{ display: "flex", justifyContent: "flex-end" }}
          >
            <Box className={classes.rightContainer}>
              <AdminSettings />
              <UserMenu />
            </Box>
          </Grid>

          {!hideSearchBar && isTablet && (
            <Grid
              container
              size={12}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
              }}
            >
              <Grid size={{ xs: 12 }} className={classes.searchBarContainer}>
                <SearchBar />
              </Grid>
            </Grid>
          )}
        </Grid>
      </Toolbar>
    </AppBar>
  );
};

const AdminSettings = memo(() => {
  const { hasPermission } = usePermissions();
  const theme = useTheme();
  const router = useRouter();

  if (!hasPermission(PERMISSIONS.SYSTEM_ADMINISTRATION.ADMINISTRATION)) {
    return null;
  }

  const handleClick = () => {
    router.navigate("/settings");
  };
  return (
    <MiIconButton
      onClick={handleClick}
      data-testid="settings-icon"
      style={{ padding: theme.spacing(0, 3, 0, 0) }}
    >
      <MiIcon path={mdiCog} color={theme.palette.common.white} size={1.2} />
    </MiIconButton>
  );
});

const useStyles = makeStyles<{ appBarColor?: string }>()(
  (theme, { appBarColor } = {}) => ({
    toolBarContainer: {
      background:
        appBarColor ??
        `conic-gradient(from 41.7deg at 100% 0%, ${theme.palette.cyan.main} 0deg, ${theme.palette.cyan.dark} 360deg)`,
      zIndex: theme.zIndex.appBar,
      flex: 1,
      [theme.breakpoints.up("sm")]: { minHeight: 60 },
    },
    leftContainer: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(3),
      marginLeft: theme.spacing(6),
      minWidth: 418,
    },
    divider: {
      borderRight: `1px solid ${theme.palette.common.white}`,
      height: 20,
      opacity: 0.8,
    },
    tenant: {
      fontSize: theme.fontSize.medium,
      color: theme.palette.common.white,
      whiteSpace: "nowrap",
      opacity: 0.8,
    },
    searchBar: {
      marginLeft: 128,
    },
    searchBarContainer: {
      padding: theme.spacing(2),
    },
    rightContainer: {
      display: "flex",
      justifyContent: "flex-end",
      marginRight: theme.spacing(6),
    },
  })
);
